import { useCallback, useEffect, useRef, useState } from 'react';
import { RetellWebClient } from 'retell-client-js-sdk';
import { createRetellWebCall } from '../../api/bookingAgent';
import { VoiceConversation } from './useVoiceConversation';
import { getWebOtpHost } from './useWebOtpAutofill';

interface UseRetellVoiceConversationArgs {
  sessionId: string | null;
  apiBase?: string;
  onCallUpdate?: () => void;
  paused?: boolean;
}

const retellClient = new RetellWebClient();

/**
 * Retell AI speech-to-speech voice loop for the booking agent.
 *
 * Audio STT/TTS is handled by Retell over WebRTC. The booking brain runs on
 * the backend custom-LLM websocket (see booking_agent/retell_llm_ws_server.py).
 */
export function useRetellVoiceConversation({
  sessionId,
  apiBase,
  onCallUpdate,
  paused = false,
}: UseRetellVoiceConversationArgs): VoiceConversation {
  const supported = typeof window !== 'undefined'
    && Boolean(window.RTCPeerConnection)
    && Boolean(navigator.mediaDevices?.getUserMedia);

  const [voiceMode, setVoiceMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isAwaitingReply, setIsAwaitingReply] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  const voiceModeRef = useRef(false);
  const pausedRef = useRef(paused);
  const actionBlockedRef = useRef(false);
  const sessionIdRef = useRef(sessionId);
  const onCallUpdateRef = useRef(onCallUpdate);
  const lastTranscriptLengthRef = useRef(0);
  const hasAgentSpokenRef = useRef(false);

  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { onCallUpdateRef.current = onCallUpdate; }, [onCallUpdate]);

  const [isGreeting, setIsGreeting] = useState(false);

  const stopCall = useCallback(() => {
    try {
      retellClient.stopCall();
    } catch {
      // noop
    }
    setIsListening(false);
    setIsSpeaking(false);
    setIsAwaitingReply(false);
  }, []);

  const stopVoiceMode = useCallback(() => {
    voiceModeRef.current = false;
    setVoiceMode(false);
    actionBlockedRef.current = false;
    lastTranscriptLengthRef.current = 0;
    hasAgentSpokenRef.current = false;
    setIsGreeting(false);
    stopCall();
    setVoiceError(null);
  }, [stopCall]);

  useEffect(() => {
    const onCallStarted = () => {
      if (!voiceModeRef.current) return;
      setVoiceError(null);
      hasAgentSpokenRef.current = false;
      setIsGreeting(true);
      setIsListening(false);
      setIsSpeaking(false);
    };
    const onCallEnded = () => {
      setIsListening(false);
      setIsSpeaking(false);
      setIsAwaitingReply(false);
      if (voiceModeRef.current) {
        voiceModeRef.current = false;
        setVoiceMode(false);
      }
    };
    const onAgentStartTalking = () => {
      setIsSpeaking(true);
      setIsListening(false);
      setIsAwaitingReply(false);
      setIsGreeting(!hasAgentSpokenRef.current);
    };
    const onAgentStopTalking = () => {
      setIsSpeaking(false);
      setIsGreeting(false);
      hasAgentSpokenRef.current = true;
      if (voiceModeRef.current && !pausedRef.current && !actionBlockedRef.current) {
        setIsListening(true);
      }
    };
    const onUpdate = (update: { transcript?: Array<{ role: string; content: string }> }) => {
      const transcript = update.transcript || [];
      if (transcript.length <= lastTranscriptLengthRef.current) {
        return;
      }
      lastTranscriptLengthRef.current = transcript.length;
      const lastEntry = transcript[transcript.length - 1];
      if (lastEntry?.role === 'user') {
        setIsAwaitingReply(true);
      }
      void Promise.resolve(onCallUpdateRef.current?.()).finally(() => {
        setIsAwaitingReply(false);
      });
    };
    const onError = (error: unknown) => {
      setVoiceError('Retell voice error. Check microphone access and try again.');
      console.error('Retell voice error', error);
      stopVoiceMode();
    };

    retellClient.on('call_started', onCallStarted);
    retellClient.on('call_ended', onCallEnded);
    retellClient.on('agent_start_talking', onAgentStartTalking);
    retellClient.on('agent_stop_talking', onAgentStopTalking);
    retellClient.on('update', onUpdate);
    retellClient.on('error', onError);

    return () => {
      retellClient.off('call_started', onCallStarted);
      retellClient.off('call_ended', onCallEnded);
      retellClient.off('agent_start_talking', onAgentStartTalking);
      retellClient.off('agent_stop_talking', onAgentStopTalking);
      retellClient.off('update', onUpdate);
      retellClient.off('error', onError);
    };
  }, [stopVoiceMode]);

  useEffect(() => {
    if (!voiceModeRef.current) return;
    if (paused || actionBlockedRef.current) {
      setIsListening(false);
      return;
    }
    if (!isSpeaking && !isAwaitingReply && !isGreeting && hasAgentSpokenRef.current) {
      setIsListening(true);
    }
  }, [paused, isSpeaking, isAwaitingReply, isGreeting]);

  const startRetellCall = useCallback(async () => {
    const activeSessionId = sessionIdRef.current;
    if (!activeSessionId) {
      setVoiceError('Start a chat session before using voice.');
      return;
    }
    try {
      const { access_token: accessToken } = await createRetellWebCall(
        activeSessionId,
        apiBase,
        getWebOtpHost(),
      );
      await retellClient.startCall({ accessToken });
    } catch {
      setVoiceError('Could not start Retell voice call. Try again or use text chat.');
      voiceModeRef.current = false;
      setVoiceMode(false);
    }
  }, [apiBase]);

  const toggleVoiceMode = useCallback(() => {
    if (voiceModeRef.current) {
      stopVoiceMode();
      return;
    }
    voiceModeRef.current = true;
    setVoiceMode(true);
    setVoiceError(null);
    hasAgentSpokenRef.current = false;
    setIsGreeting(true);
    setIsListening(false);
    void startRetellCall();
  }, [startRetellCall, stopVoiceMode]);

  const resumeListening = useCallback(() => {
    setIsAwaitingReply(false);
    if (voiceModeRef.current && !pausedRef.current && !actionBlockedRef.current) {
      setIsListening(true);
    }
  }, []);

  const setActionBlocked = useCallback((blocked: boolean) => {
    actionBlockedRef.current = blocked;
    if (blocked) {
      setIsListening(false);
    }
  }, []);

  const speak = useCallback((_text: string, _apiBase?: string) => {
    // ponytail: Retell speaks agent replies over WebRTC; HTTP TTS is unused in S2S mode.
  }, []);

  return {
    supported,
    voiceMode,
    isListening,
    isSpeaking,
    isAwaitingReply,
    isGreeting,
    voiceError,
    toggleVoiceMode,
    stopVoiceMode,
    resumeListening,
    setActionBlocked,
    speak,
  };
}
